import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useNotifications } from 'hooks/useNotifications';
import {
  IAnnouncementCreateRequest,
  IAnnouncementResponse,
  TAnnouncementPublishFor,
  TAnnouncementAppearIn,
  TAnnouncementRestrictType,
} from 'services/BackendApi/types/AnnouncementResponse';
import { useCreateAnnouncement, useAnnouncement, useUpdateAnnouncement } from './useAnnouncementQueries';
import { parseAsUTC } from 'utils/date.utc';
import { isBlankHtml } from 'utils';
import { IMultiSearchDropdownOption } from 'ui/MultiSearchDropdown/MultiSearchDropdownOptions';

// Local state structure for form
interface IAnnouncementEditState {
  hotelUuid: string;
  title: string;
  description: string;
  restrictType: TAnnouncementRestrictType;
  countryCodes: string[];
  travelCompanyUuids: string[];
  isDraft: boolean;
  publishFor: TAnnouncementPublishFor;
  activeFrom: string;
  activeTo: string;
  appearIn: TAnnouncementAppearIn[];
}

interface IValidationErrors {
  hotelUuid?: boolean;
  title?: boolean;
  description?: boolean;
  activeFrom?: boolean;
  activeTo?: boolean;
  appearIn?: boolean;
  targeting?: boolean; // For countryCodes OR travelCompanyUuids
}

interface IUseAnnouncementEditProps {
  announcementUuid: string | null; // null for create mode
}

export const useAnnouncementEdit = ({ announcementUuid }: IUseAnnouncementEditProps) => {
  const history = useHistory();
  const { showSuccessNotification, showErrorNotification } = useNotifications();

  const createAnnouncementMutation = useCreateAnnouncement();
  const updateAnnouncementMutation = useUpdateAnnouncement();

  // Fetch existing announcement data if in edit mode
  const { data: existingAnnouncement, isLoading: isLoadingAnnouncement, error: announcementError } = useAnnouncement(
    announcementUuid
  );

  // State management
  const [announcementEditState, setAnnouncementEditState] = useState<IAnnouncementEditState>({
    hotelUuid: '',
    title: '',
    description: '',
    restrictType: 'BY_COUNTRIES', // Default to country-based targeting
    countryCodes: [],
    travelCompanyUuids: [],
    isDraft: true, // Default to draft
    publishFor: 'ALL',
    activeFrom: '',
    activeTo: '',
    appearIn: [], // At least one required
  });

  const [isEditedWithoutSaving, setIsEditedWithoutSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<IValidationErrors>({});
  const [initialTravelCompanyOptions, setInitialTravelCompanyOptions] = useState<IMultiSearchDropdownOption[]>([]);

  // Initialize state with existing announcement data (for edit mode)
  useEffect(() => {
    if (existingAnnouncement && announcementUuid) {
      // Check if we have full details (IAnnouncementResponse) or just list item
      const hasFullDetails = 'description' in existingAnnouncement;

      if (hasFullDetails) {
        const announcement = existingAnnouncement as IAnnouncementResponse;
        // Convert ISO date strings to YYYY-MM-DD format for date inputs
        const formatDateForInput = (dateString: string | null): string => {
          if (!dateString) return '';
          try {
            return parseAsUTC(dateString);
          } catch {
            return '';
          }
        };

        setAnnouncementEditState({
          hotelUuid: announcement.hotel.uuid || '',
          title: announcement.title || '',
          description: announcement.description || '',
          restrictType: announcement.restrictType || 'BY_COUNTRIES',
          countryCodes: announcement.countries || [],
          travelCompanyUuids: announcement.travelCompanies?.map(tc => tc.uuid) ?? [],
          isDraft: announcement.isDraft ?? true,
          publishFor: announcement.publishFor || 'ALL',
          activeFrom: formatDateForInput(announcement.activeFrom),
          activeTo: formatDateForInput(announcement.activeTo),
          appearIn: announcement.appearIn || [],
        });
        setInitialTravelCompanyOptions(announcement.travelCompanies?.map(tc => ({ value: tc.uuid, label: tc.name })) ?? []);
      }
      setIsEditedWithoutSaving(false);
    }
  }, [existingAnnouncement, announcementUuid]);

  // Helper to clear individual validation error
  const clearValidationError = useCallback((field: keyof IValidationErrors) => {
    setValidationErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  // Generic update function
  const updateAnnouncementData = useCallback((updates: Partial<IAnnouncementEditState>) => {
    setAnnouncementEditState(prevState => ({
      ...prevState,
      ...updates,
    }));
    setIsEditedWithoutSaving(true);
  }, []);

  // Helper function to clean payload for API submission
  const cleanPayloadForApi = useCallback((data: IAnnouncementEditState): IAnnouncementCreateRequest => {
    // ========================================
    // BUSINESS REQUIREMENT (2025-11-18):
    // If user selects "BY_TRAVEL_COMPANIES" mode but doesn't select any TCs,
    // silently switch to "BY_COUNTRIES" mode with empty country list.
    // This matches the behavior of selecting "BY_COUNTRIES" with no countries selected,
    // which means "all countries". Commented clearly for easy reversal if business changes mind.
    // ========================================
    const effectiveRestrictType =
      data.restrictType === 'BY_TRAVEL_COMPANIES' && data.travelCompanyUuids.length === 0
        ? 'BY_COUNTRIES'  // Auto-switch to BY_COUNTRIES when no TCs selected
        : data.restrictType;

    return {
      type: 'HOTEL', // MVP only supports HOTEL type
      hotelUuid: data.hotelUuid || null,
      title: data.title.trim(),
      description: data.description.trim(),
      restrictType: effectiveRestrictType,
      // Backend expects 'countries' (array of country codes) not 'countryCodes'
      // When auto-switched from BY_TRAVEL_COMPANIES, countryCodes will be [], meaning "all countries"
      countries: effectiveRestrictType === 'BY_COUNTRIES' ? data.countryCodes : undefined,
      // Backend expects 'travelCompanies' (array of TC UUIDs) not 'travelCompanyUuids'
      // Only send if we're genuinely in BY_TRAVEL_COMPANIES mode with selections
      travelCompanies: effectiveRestrictType === 'BY_TRAVEL_COMPANIES' && data.travelCompanyUuids.length > 0 ? data.travelCompanyUuids : undefined,
      isDraft: data.isDraft,
      publishFor: data.publishFor,
      activeFrom: data.activeFrom && data.activeFrom.trim() ? parseAsUTC(data.activeFrom) : undefined,
      activeTo: data.activeTo && data.activeTo.trim() ? parseAsUTC(data.activeTo) : undefined,
      appearIn: data.appearIn,
    };
  }, []);

  // Save function with validation
  const handleSave = useCallback(async () => {
    const newValidationErrors: IValidationErrors = {};

    // Always required fields
    if (!announcementEditState.hotelUuid.trim()) {
      newValidationErrors.hotelUuid = true;
    }
    if (!announcementEditState.title.trim()) {
      newValidationErrors.title = true;
    }
    if (isBlankHtml(announcementEditState.description)) {
      newValidationErrors.description = true;
    }
    if (!announcementEditState.appearIn || announcementEditState.appearIn.length === 0) {
      newValidationErrors.appearIn = true;
    }

    // Active From is required when not a draft
    if (!announcementEditState.isDraft && !announcementEditState.activeFrom?.trim()) {
      newValidationErrors.activeFrom = true;
    }

    // ========================================
    // BUSINESS REQUIREMENT (2025-11-10):
    // activeTo should NEVER be mandatory
    // Uncomment below if requirement changes
    // ========================================
    // if (!announcementEditState.isDraft && !announcementEditState.activeTo?.trim()) {
    //   newValidationErrors.activeTo = true;
    // }

    // ========================================
    // BUSINESS REQUIREMENT (2025-11-18 - LATER REVISED SAME DAY):
    // Travel Companies validation REMOVED - when user selects BY_TRAVEL_COMPANIES but
    // leaves TC list empty, we silently switch to BY_COUNTRIES with empty country list
    // (meaning "all countries"). See cleanPayloadForApi function for implementation.
    // Commented out for easy reversal if business changes mind again:
    // ========================================
    // if (announcementEditState.restrictType === 'BY_TRAVEL_COMPANIES') {
    //   if (!announcementEditState.travelCompanyUuids || announcementEditState.travelCompanyUuids.length === 0) {
    //     newValidationErrors.targeting = true;
    //   }
    // }

    if (Object.keys(newValidationErrors).length > 0) {
      setValidationErrors(newValidationErrors);

      // Scroll to the first validation error field
      const firstErrorField = Object.keys(newValidationErrors)[0];
      const element = document.querySelector(`[data-validation-id="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }

      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (announcementUuid) {
        // Update existing announcement
        const updateData = cleanPayloadForApi(announcementEditState);
        await updateAnnouncementMutation.mutateAsync({
          uuid: announcementUuid,
          announcementData: updateData,
        });
        showSuccessNotification('Announcement updated successfully');
        history.push('/announcements');
      } else {
        // Create new announcement
        const announcementData = cleanPayloadForApi(announcementEditState);
        await createAnnouncementMutation.mutateAsync(announcementData);
        showSuccessNotification('Announcement created successfully');
        history.push('/announcements');
      }

      setIsEditedWithoutSaving(false);
    } catch (err) {
      setError(err.message || 'Failed to save announcement');
      showErrorNotification('Failed to save announcement');
    } finally {
      setIsLoading(false);
    }
  }, [
    announcementEditState,
    announcementUuid,
    createAnnouncementMutation,
    updateAnnouncementMutation,
    showSuccessNotification,
    showErrorNotification,
    history,
    cleanPayloadForApi,
  ]);

  // Cancel function
  const handleCancel = useCallback(() => {
    if (isEditedWithoutSaving) {
      // LeaveWithoutSavingModal will handle this
      return;
    }
    history.push('/announcements');
  }, [isEditedWithoutSaving, history]);

  // Individual update functions for each field
  const updateHotelUuid = useCallback(
    (hotelUuid: string) => {
      updateAnnouncementData({ hotelUuid });
      if (hotelUuid.trim()) {
        clearValidationError('hotelUuid');
      }
    },
    [updateAnnouncementData, clearValidationError]
  );

  const updateTitle = useCallback(
    (title: string) => {
      updateAnnouncementData({ title });
      if (title.trim()) {
        clearValidationError('title');
      }
    },
    [updateAnnouncementData, clearValidationError]
  );

  const updateDescription = useCallback(
    (description: string) => {
      updateAnnouncementData({ description });
      if (!isBlankHtml(description)) {
        clearValidationError('description');
      }
    },
    [updateAnnouncementData, clearValidationError]
  );

  const updateRestrictType = useCallback(
    (restrictType: TAnnouncementRestrictType) => {
      updateAnnouncementData({ restrictType });
      clearValidationError('targeting');
    },
    [updateAnnouncementData, clearValidationError]
  );

  const updateCountryCodes = useCallback(
    (countryCodes: string[]) => {
      updateAnnouncementData({ countryCodes });
      if (countryCodes.length > 0) {
        clearValidationError('targeting');
      }
    },
    [updateAnnouncementData, clearValidationError]
  );

  const updateTravelCompanyUuids = useCallback(
    (travelCompanyUuids: string[]) => {
      updateAnnouncementData({ travelCompanyUuids });
      if (travelCompanyUuids.length > 0) {
        clearValidationError('targeting');
      }
    },
    [updateAnnouncementData, clearValidationError]
  );

  const resetCountryCodes = useCallback(() => {
    updateAnnouncementData({ countryCodes: [] });
  }, [updateAnnouncementData]);

  const updateIsDraft = useCallback(
    (isDraft: boolean) => {
      updateAnnouncementData({ isDraft });
    },
    [updateAnnouncementData]
  );

  const updatePublishFor = useCallback(
    (publishFor: TAnnouncementPublishFor) => {
      updateAnnouncementData({ publishFor });
    },
    [updateAnnouncementData]
  );

  const updateActiveFrom = useCallback(
    (activeFrom: string) => {
      updateAnnouncementData({ activeFrom });
      if (activeFrom.trim()) {
        clearValidationError('activeFrom');
      }
    },
    [updateAnnouncementData, clearValidationError]
  );

  const updateActiveTo = useCallback(
    (activeTo: string) => {
      updateAnnouncementData({ activeTo });
      if (activeTo.trim()) {
        clearValidationError('activeTo');
      }
    },
    [updateAnnouncementData, clearValidationError]
  );

  const updateAppearIn = useCallback(
    (appearIn: TAnnouncementAppearIn[]) => {
      updateAnnouncementData({ appearIn });
      if (appearIn.length > 0) {
        clearValidationError('appearIn');
      }
    },
    [updateAnnouncementData, clearValidationError]
  );

  return {
    announcementEditState,
    isEditedWithoutSaving,
    isLoading: isLoading || isLoadingAnnouncement,
    error: error || (announcementError ? 'Failed to load announcement' : null),
    validationErrors,
    initialTravelCompanyOptions,
    handleSave,
    handleCancel,
    // Field update functions
    updateHotelUuid,
    updateTitle,
    updateDescription,
    updateRestrictType,
    updateCountryCodes,
    resetCountryCodes,
    updateTravelCompanyUuids,
    updateIsDraft,
    updatePublishFor,
    updateActiveFrom,
    updateActiveTo,
    updateAppearIn,
  };
};
